import * as React from 'react';
import gql from 'graphql-tag';
import {graphql, InjectedGraphQLProps} from 'react-apollo';
import {Component, deps, inject, observer} from '../../lib/component';
import {style} from 'typestyle';
import {important, percent, rem, viewHeight} from 'csx';
import {centerCenter, end, endJustified, horizontal} from 'csstips';
import {ArtistTileGrid, ArtistTileItem} from '../tile-grid';
import {gradientDark} from '../../theme/gradient';
import {PaginationInput} from '../../types';
import maybe from '../../lib/maybe';
import {Artist, FindFiltersInput, FindResults} from '../../types/graphql';
import InlinePreviewImage from '../inline-preview/image';
import {ImageAspect} from '../../constants';
import {shadowFirm} from '../../theme/shadow';
import {colorContrast, colorWhite} from '../../theme/color';
import FullWidthText from '../full-width-text';
import {resetAnchorStyles} from '../../theme/reset';
import {FavouriteArtistButtonCompat} from '../compat/favourite-artist-button-compat';

@inject(deps) @observer
class ArtistGrid extends Component<Props & InjectedGraphQLProps<Data>> {
  render() {
    const {data} = this.props;
    const {handleClickLink} = this.props.controller.ui;
    const {asArticleImage} = this.props.model.image;
    const {windowSizeHash} = this.props.model.ui;

    const tiles = maybe(
      () => data.find!.artists!.results!,
      []
    ).map(ArtistGrid.mapArtist);

    // TODO wire this up when we need it
    const pagination = maybe(
      () => data.find!.artists!.pagination!,
      null
    );

    return (
      <div className={ArtistGrid.styles.container}>
        <ArtistTileGrid
          tiles={tiles}
          loading={data.loading}
          loadingTiles={16}
          loadingLight={true}
          renderTile={(itemClass: string, tile: ArtistTileItem, index: number) => (
            <a key={index + tile.slug} className={itemClass} title={tile.name} href={`/music/artists/${tile.slug}`} onClick={handleClickLink}>
              <InlinePreviewImage
                aspect={ImageAspect.WIDESCREEN}
                innerClassName={ArtistGrid.styles.preview}
                backgroundClassName={tile.model.active ? void 0 : ArtistGrid.styles.grayscale}
                children={(
                  <div>
                    <FavouriteArtistButtonCompat className={ArtistGrid.styles.favourite} artist={tile.model}/>
                    <FullWidthText refresh={windowSizeHash}>{tile.name}</FullWidthText>
                  </div>
                )}
                image={asArticleImage(tile.images.identity, tile.name)}
              />
              {!tile.model.active && (
                <div className={ArtistGrid.styles.comingSoon}>Dropping soon</div>
              )}
            </a>
          )}
        />
      </div>
    )
  }

  static mapArtist = (artist: Artist): ArtistTileItem => ({
    name: artist.name,
    slug: artist.slug,
    images: artist.images,
    model: artist
  });

  static styles = {
    container: style({
      background: gradientDark,
    }),
    loading: style({
      ...centerCenter,
      width: important('100%'),
      height: viewHeight(40)
    }),
    preview: style({
      ...horizontal,
      alignItems: important('flex-end'),
      justifyContent: important('flex-start'),
      padding: rem(1),
      overflow: 'hidden',
      $nest: {
        '& > div': {
          ...horizontal,
          width: percent(100),
        },
        '& > div > span': {
          flex: 1,
          width: percent(100),
          textOverflow: 'ellipsis',
          textAlign: 'left',
          color: colorWhite.toString(),
          textShadow: shadowFirm
        }
      }
    }),
    grayscale: style({
      filter: 'grayscale(1) contrast(1.5)'
    }),
    link: style({
      color: colorWhite.toString(),
      $nest: {
        ...resetAnchorStyles(colorWhite.toString())
      }
    }),
    favourite: style({
      position: 'absolute',
      right: rem(1),
      top: rem(1),
    }),
    comingSoon: style({
      position: 'absolute',
      left: rem(1),
      top: rem(1),
      fontSize: rem(0.75),
      borderRadius: rem(0.125),
      color: colorWhite.toString(),
      textTransform: 'uppercase',
      fontWeight: 500,
      padding: '0 0.25rem',
      background: colorContrast.fade(0.9).toString()
    })
  };
}

type Props = {
  pagination: PaginationInput;
  filters: FindFiltersInput;
}

type Item = {
  identity: string;
  name: string;
  slug: string;
  images: {
    identity: string;
  }
}

type Data = {
  find: FindResults
}

export default ArtistGrid;

export const ConnectedArtistGrid = graphql(gql`
  query (
    $filters: FindFiltersInput
    $pagination: PaginationInput!
  ) {
    find(filters: $filters) {
      artists(pagination: $pagination) {
        results {
          identity
          name
          slug
          active
          images {
            identity
          }
        }
      }
    }
  }
`, {
  options: (variables: Props) => ({
    variables,
    // fetchPolicy: 'network-only'
  }),
})(ArtistGrid);